
import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { MobileNavigation } from "./MobileNavigation";
import { useDeviceInfo } from "@/hooks/use-device-info";
import { useIdleLogout } from "@/hooks/use-idle-logout";
import { AgentChat } from "@/components/agent/AgentChat";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { getResponsiveContainer, getResponsiveSpacing } from "@/lib/utils";

export interface ResponsiveLayoutProps {
  children?: React.ReactNode;
  showSidebar?: boolean;
  sidebarCollapsed?: boolean;
}

export function Layout({ showSidebar = true }: ResponsiveLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isMobile, isTablet, screenSize, orientation } = useDeviceInfo();
  useIdleLogout();

  const handleMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when screen size changes to tablet/desktop
  useEffect(() => {
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);

  // Handle orientation changes on mobile
  useEffect(() => {
    // Only close menu when orientation actually changes, not when menu state changes
    if (isMobile && isMobileMenuOpen) {
      console.log('Layout: Closing menu due to orientation change');
      setIsMobileMenuOpen(false);
    }
  }, [orientation]); // Remove isMobileMenuOpen from dependencies

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop/Tablet Sidebar - Hidden on mobile */}
      {showSidebar && !isMobile && <Sidebar />}
      
      {/* Mobile Navigation Drawer */}
      {showSidebar && (
        <MobileNavigation 
          isOpen={isMobileMenuOpen} 
          onClose={handleMobileMenuClose} 
        />
      )}
      
      {/* Main Content Area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Lapsed subscription — warn, link to /pay, never block */}
        <SubscriptionBanner />
        <Header 
          onMenuToggle={handleMenuToggle}
          showMenuButton={isMobile && showSidebar}
        />
        
        {/* Responsive Main Content Container */}
        <main 
          className={`
            flex-1 overflow-y-auto overflow-x-hidden min-w-0
            ${getResponsiveSpacing({ 
              mobile: '4', 
              tablet: '6', 
              desktop: '8',
              property: 'p' 
            })}
            ${screenSize === 'mobile' ? 'pb-safe-area-inset-bottom' : ''}
          `}
          role="main"
          aria-label="Main content"
        >
          {/* Responsive Container */}
          <div className={`
            w-full max-w-none
            ${screenSize === 'mobile' ? 'min-h-[calc(100vh-4rem)]' : ''}
            ${screenSize === 'tablet' ? 'max-w-none' : ''}
            ${screenSize === 'desktop' ? 'max-w-none' : ''}
          `}>
            <Outlet />
          </div>
        </main>
      </div>
      
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={handleMobileMenuClose}
          aria-hidden="true"
        />
      )}

      {/* AI Assistant — floating chat button on all pages */}
      <AgentChat />
    </div>
  );
}
